import { getVersions, getFeatureRequests, getBugReports } from "../data/store";
import type { Version } from "../types";

export default function Releases() {
  const versions = getVersions();
  const features = getFeatureRequests();
  const bugs = getBugReports();

  // Group versions that have linked items as "releases"
  const releases = versions.filter(
    (v) => v.linkedFeatures.length > 0 || v.linkedBugs.length > 0 || v.stage === "prod"
  );

  function getReleaseNotes(version: Version) {
    const linkedFeatures = features.filter((f) => version.linkedFeatures.includes(f.id));
    const linkedBugs = bugs.filter((b) => version.linkedBugs.includes(b.id));
    return { features: linkedFeatures, bugs: linkedBugs };
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Releases</h2>
        <p>Release notes for each version — features shipped and bugs fixed.</p>
      </div>

      {releases.length === 0 ? (
        <div className="card">
          <p style={{ color: "var(--text-muted)", textAlign: "center" }}>
            No releases yet. Link features and bugs to versions to generate release notes.
          </p>
        </div>
      ) : (
        releases.map((v) => {
          const { features: vFeatures, bugs: vBugs } = getReleaseNotes(v);
          return (
            <div key={v.id} className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 style={{ display: "inline" }}>v{v.version}</h3>
                  <span
                    className={`badge ${v.stage === "prod" ? "badge-success" : v.stage === "beta" ? "badge-warning" : "badge-info"}`}
                    style={{ marginLeft: "0.75rem" }}
                  >
                    {v.stage}
                  </span>
                  {v.status === "rolled-back" && (
                    <span className="badge badge-danger" style={{ marginLeft: "0.5rem" }}>rolled back</span>
                  )}
                </div>
                <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                  {new Date(v.timestamp).toLocaleString()}
                </span>
              </div>

              <p style={{ marginTop: "0.5rem", fontSize: "0.9rem", color: "var(--text-muted)" }}>
                {v.commitMessage}
              </p>

              <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                {vFeatures.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                      ✨ Features ({vFeatures.length})
                    </h4>
                    <ul style={{ paddingLeft: "1.25rem", fontSize: "0.9rem" }}>
                      {vFeatures.map((f) => (
                        <li key={f.id} style={{ marginBottom: "0.25rem" }}>{f.title}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {vBugs.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                      🐛 Bug Fixes ({vBugs.length})
                    </h4>
                    <ul style={{ paddingLeft: "1.25rem", fontSize: "0.9rem" }}>
                      {vBugs.map((b) => (
                        <li key={b.id} style={{ marginBottom: "0.25rem" }}>{b.title}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
